import { useMemo } from "react";
import { useCurrentUser } from "@/context/UserProvider";
import { userIsOutsideUser } from "@/lib/grantSuiteAccess";
import { Bookmark, Download, Loader2, Plus, Search, Sparkles, X } from "lucide-react";

import {
  downloadGrantOpportunitiesCsv,
  GRANT_OPPORTUNITY_STATUS_LABELS,
  type GrantOpportunityStatus,
} from "@/api/grant_opportunities";
import { GrantOpportunityDetail } from "@/components/grant_suite/GrantOpportunityDetail";
import { GrantTrackingCreateDialog } from "@/components/grant_suite/GrantTrackingCreateDialog";
import { SuiteTrackingLayout } from "@/components/workflow_tracking/SuiteTrackingLayout";
import {
  formatGrantDeadline,
  grantOpportunityListMeta,
  isGrantOverdue,
  pipelineSummaryStats,
} from "@/components/grant_suite/grantSuiteDisplay";
import type { GrantOpportunityFocusRequest } from "@/components/grant_suite/grantSuiteNavigation";
import { formatRelativeTime } from "@/components/module_summary/formatRelativeTime";
import { ModuleSummaryStrip } from "@/components/module_summary/ModuleSummaryStrip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useMunicipality } from "@/context/MunicipalityContext";
import { useGrantTrack, type GrantTrackListFilter, type GrantTrackStatusFilter } from "@/hooks/useGrantTrack";
import { useToast } from "@/hooks/useToast";
import { cn } from "@/lib/utils";

// Radix Select rejects an empty string as an item value, so team scope needs a
// sentinel rather than "".
const TEAM_SCOPE_VALUE = "team";

type GrantTrackingPanelProps = {
  defaultFilter?: GrantTrackListFilter;
  onGoToDiscover?: () => void;
  focusRequest?: GrantOpportunityFocusRequest | null;
  onFocusRequestHandled?: () => void;
};

export function GrantTrackingPanel({
  // ENG-1691: land on Pursuing (day-to-day work) instead of Saved, which is
  // usually empty and reads as broken.
  defaultFilter = "pursuing",
  onGoToDiscover,
  focusRequest = null,
  onFocusRequestHandled,
}: GrantTrackingPanelProps) {
  const { municipality, municipalities, isLoading: municipalitiesLoading } = useMunicipality();
  const currentUser = useCurrentUser();
  // Outside collaborators are view-only (ENG-1820): hide the create and
  // export affordances whose requests the server would reject anyway.
  const outsideUser = userIsOutsideUser(currentUser);
  const { toast } = useToast();
  const municipalityId = municipality?.id;

  const track = useGrantTrack({
    municipalityId,
    defaultFilter,
    focusRequest,
    onFocusRequestHandled,
  });

  const summaryStats = useMemo(() => pipelineSummaryStats(track.allOpportunities), [track.allOpportunities]);

  const activeDepartment = useMemo(
    () => track.departments.find((department) => department.id === track.departmentId) ?? null,
    [track.departments, track.departmentId],
  );

  // ENG-1824: the AI Partner entry path is flagged per team. Outside
  // collaborators are view-only, so they never see it either.
  const aiPartnerAvailable = Boolean(municipality?.ai_partner) && !outsideUser;

  if (municipalitiesLoading || municipalities.length === 0 || !municipalityId) {
    return (
      <SuiteTrackingLayout
        municipalitiesLoading={municipalitiesLoading}
        municipalitiesCount={municipalities.length}
        municipalitySelected={Boolean(municipalityId)}
        emptyIcon={Bookmark}
        selectMunicipalityMessage="Select a municipality above to view saved grants and active pursuits."
        summaryStrip={null}
        sidebar={null}
        detail={null}
      />
    );
  }

  return (
    <SuiteTrackingLayout
      testId="grant-tracking-panel"
      municipalitiesLoading={false}
      municipalitiesCount={municipalities.length}
      municipalitySelected
      emptyIcon={Bookmark}
      selectMunicipalityMessage="Select a municipality above to view saved grants and active pursuits."
      summaryStrip={
        <ModuleSummaryStrip
          className="border-b"
          stats={summaryStats}
          maxStats={6}
          loading={track.isLoading}
          action={
            outsideUser
              ? undefined
              : { label: "Add manually", onClick: () => track.setCreateOpen(true) }
          }
          trailing={
            outsideUser ? null : (
            // ENG-1696: the button exports tracker/CRM rows, not File Center
            // documents. Clarify with copy + a tooltip so it's obvious without
            // opening the file.
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs"
                    disabled={track.exportingCsv}
                    onClick={async () => {
                      if (!municipalityId || track.exportingCsv) return;
                      track.setExportingCsv(true);
                      try {
                        await downloadGrantOpportunitiesCsv(municipalityId);
                      } catch {
                        toast({
                          title: "Export failed",
                          description: "We could not download your pipeline CSV. Please try again.",
                          variant: "destructive",
                        });
                      } finally {
                        track.setExportingCsv(false);
                      }
                    }}
                  >
                    {track.exportingCsv ? (
                      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Download className="mr-2 h-3.5 w-3.5" />
                    )}
                    {track.exportingCsv ? "Exporting…" : "Export tracker (CSV)"}
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  Downloads your tracker rows (title, status, owner, summary) as a CSV —
                  not documents or attachments.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            )
          }
        />
      }
      sidebar={
        <>
          <div className="space-y-3 border-b p-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search title or notes…"
                value={track.search}
                onChange={(event) => track.setSearch(event.target.value)}
                aria-label="Search grant opportunities"
              />
            </div>

            {track.departments.length > 0 ? (
              <div className="space-y-2">
                <Select
                  value={track.departmentId === null ? TEAM_SCOPE_VALUE : String(track.departmentId)}
                  onValueChange={(value) =>
                    track.setDepartmentId(value === TEAM_SCOPE_VALUE ? null : Number(value))
                  }
                >
                  <SelectTrigger aria-label="Filter by department">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TEAM_SCOPE_VALUE}>Team opportunities</SelectItem>
                    {track.departments.map((department) => (
                      <SelectItem key={department.id} value={String(department.id)}>
                        {department.name}
                        {department.member ? "" : " (not joined)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {activeDepartment ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    disabled={track.membershipMutation.isPending}
                    onClick={() =>
                      track.membershipMutation.mutate({
                        department: activeDepartment,
                        join: !activeDepartment.member,
                      })
                    }
                  >
                    {activeDepartment.member
                      ? `Leave ${activeDepartment.name}`
                      : `Join ${activeDepartment.name}`}
                  </Button>
                ) : null}
              </div>
            ) : null}

            <ToggleGroup
              type="single"
              value={track.filter}
              onValueChange={(value) => {
                if (value) track.setFilter(value as GrantTrackListFilter);
              }}
              className="grid w-full grid-cols-2"
              variant="outline"
              size="sm"
            >
              <ToggleGroupItem value="pursuing" aria-label="Actively pursuing">
                Pursuing
              </ToggleGroupItem>
              <ToggleGroupItem value="watchlist" aria-label="Saved to watchlist">
                Saved
              </ToggleGroupItem>
            </ToggleGroup>

            {track.filter === "pursuing" ? (
              <Select
                value={track.statusFilter}
                onValueChange={(value) => track.setStatusFilter(value as GrantTrackStatusFilter)}
              >
                <SelectTrigger aria-label="Filter by status">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {track.pipelineStatusOrder.map((status) => (
                    <SelectItem key={status} value={status}>
                      {GRANT_OPPORTUNITY_STATUS_LABELS[status]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}

            {track.filtered.length > 0 ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Checkbox
                  id="select-all-opportunities"
                  checked={track.selectedIds.length > 0 && track.selectedIds.length === track.filtered.length}
                  onCheckedChange={(checked) => track.toggleSelectAll(checked === true)}
                />
                <Label htmlFor="select-all-opportunities" className="text-xs font-normal">
                  Select all
                </Label>
              </div>
            ) : null}

            {track.selectedIds.length > 0 ? (
              <div
                className="space-y-3 rounded-lg border bg-background p-3"
                data-testid="grant-bulk-actions"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-medium">{track.selectedIds.length} selected</p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    aria-label="Clear selection"
                    onClick={() => track.setSelectedIds([])}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {track.filter === "watchlist" ? (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={track.bulkUpdateMutation.isPending}
                      onClick={() =>
                        track.bulkUpdateMutation.mutate({ watching: false, status: "preparing" })
                      }
                    >
                      Start pursuing
                    </Button>
                  ) : null}
                  <Select
                    value={track.bulkStatus}
                    onValueChange={(value) => track.setBulkStatus(value as GrantOpportunityStatus)}
                  >
                    <SelectTrigger className="h-8 w-[9.5rem]" aria-label="Bulk status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {track.pipelineStatusOrder.map((status) => (
                        <SelectItem key={status} value={status}>
                          {GRANT_OPPORTUNITY_STATUS_LABELS[status]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={track.bulkUpdateMutation.isPending}
                    onClick={() => track.bulkUpdateMutation.mutate({ status: track.bulkStatus, watching: false })}
                  >
                    Set status
                  </Button>
                  <Input
                    type="date"
                    className="h-8 w-[9.5rem]"
                    value={track.bulkDeadline}
                    onChange={(event) => track.setBulkDeadline(event.target.value)}
                    aria-label="Bulk deadline"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!track.bulkDeadline || track.bulkUpdateMutation.isPending}
                    onClick={() =>
                      track.bulkUpdateMutation.mutate({
                        deadline_at: new Date(track.bulkDeadline).toISOString(),
                      })
                    }
                  >
                    Set deadline
                  </Button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="relative min-h-0 flex-1">
            <ScrollArea className="h-full min-h-0">
              <div className="space-y-2 p-3">
                {track.isLoading && (
                  <div className="flex items-center gap-2 px-2 py-6 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading opportunities…
                  </div>
                )}

                {track.error && (
                  <p className="px-2 text-sm text-destructive">Could not load grant opportunities.</p>
                )}

                {!track.isLoading &&
                  track.filtered.map((opp) => {
                    const meta = grantOpportunityListMeta(opp);
                    const isSelected = track.selectedIds.includes(opp.id);
                    return (
                      <div
                        key={opp.id}
                        className={cn(
                          "flex gap-2 rounded-lg border bg-background p-3 transition-colors hover:bg-muted/40",
                          track.selected?.id === opp.id && "border-primary/60 bg-primary/5 ring-1 ring-primary/20",
                        )}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => track.toggleSelected(opp.id, checked === true)}
                          aria-label={`Select ${opp.title}`}
                          className="mt-0.5"
                        />
                        <button
                          type="button"
                          className="min-w-0 flex-1 text-left"
                          onClick={() => track.setSelectedId(opp.id)}
                        >
                          <span className="line-clamp-2 text-sm font-medium leading-snug">{opp.title}</span>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span
                              className={cn(
                                "inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium",
                                meta.pillClass,
                              )}
                            >
                              {meta.label}
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              {formatGrantDeadline(opp.deadline_at)}
                            </span>
                            {isGrantOverdue(opp) ? (
                              <Badge variant="destructive" className="h-5 px-1.5 text-[10px] font-medium">
                                Overdue
                              </Badge>
                            ) : null}
                          </div>
                          <p className="mt-1 text-[11px] text-muted-foreground">
                            Updated {formatRelativeTime(opp.updated_at)}
                          </p>
                          {opp.assigned_to_name ? (
                            <p className="mt-1 truncate text-[11px] text-muted-foreground">
                              Owner: {opp.assigned_to_name}
                            </p>
                          ) : null}
                        </button>
                      </div>
                    );
                  })}

                {!track.isLoading && track.filtered.length === 0 && (
                  <div className="rounded-lg border border-dashed bg-background px-4 py-8 text-center">
                    <Bookmark className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />
                    <p className="text-sm font-medium">
                      {track.filter === "watchlist" ? "Nothing saved yet" : "Nothing in pursuit yet"}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {track.filter === "watchlist"
                        ? "Save programs from Discover to evaluate later, or add a grant manually."
                        : "Start pursuing a saved grant, or add one directly to your pipeline."}
                    </p>
                    {!outsideUser && (
                      <div className="mt-4 flex flex-wrap justify-center gap-2">
                        {onGoToDiscover && (
                          <Button size="sm" variant="default" onClick={onGoToDiscover}>
                            <Sparkles className="mr-2 h-4 w-4" />
                            Go to Discover
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => track.setCreateOpen(true)}>
                          <Plus className="mr-2 h-4 w-4" />
                          Add manually
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </>
      }
      detail={
        track.selected ? (
          <GrantOpportunityDetail
            municipalityId={municipalityId}
            fileViewerEnabled={municipality?.file_viewer_access === true}
            opportunity={track.selected}
            onUpdate={(patch) => track.updateMutation.mutate({ id: track.selected!.id, patch })}
            onRequestDelete={() => track.setDeleteTarget(track.selected)}
            updating={track.updateMutation.isPending}
            rejectedUpdate={track.rejectedUpdate}
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 bg-background p-8 text-center text-muted-foreground">
            <Bookmark className="h-10 w-10 opacity-30" />
            <p className="text-sm">Select an opportunity from the list or add a new one.</p>
          </div>
        )
      }
      footer={
        <>
          <GrantTrackingCreateDialog
            open={track.createOpen}
            onOpenChange={track.setCreateOpen}
            aiPartnerAvailable={aiPartnerAvailable}
            aiPartner={track.formAiPartner}
            onAiPartnerChange={track.setFormAiPartner}
            engagementType={track.formEngagementType}
            onEngagementTypeChange={track.setFormEngagementType}
            outsideBeneficiary={track.formOutsideBeneficiary}
            onOutsideBeneficiaryChange={track.setFormOutsideBeneficiary}
            scopeLabel={activeDepartment ? activeDepartment.name : "the whole team"}
            title={track.formTitle}
            onTitleChange={track.setFormTitle}
            url={track.formUrl}
            onUrlChange={track.setFormUrl}
            notes={track.formNotes}
            onNotesChange={track.setFormNotes}
            deadline={track.formDeadline}
            onDeadlineChange={track.setFormDeadline}
            watching={track.formWatching}
            onWatchingChange={track.setFormWatching}
            saving={track.createMutation.isPending}
            onSubmit={track.submitCreate}
          />

          <AlertDialog
            open={Boolean(track.deleteTarget)}
            onOpenChange={(open) => !open && track.setDeleteTarget(null)}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove this opportunity?</AlertDialogTitle>
                <AlertDialogDescription>
                  {track.deleteTarget
                    ? `"${track.deleteTarget.title}" will be removed from tracking. Comments and document links will also be deleted.`
                    : "This action cannot be undone."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={track.deleteMutation.isPending}
                  onClick={() => track.deleteTarget && track.deleteMutation.mutate(track.deleteTarget.id)}
                >
                  {track.deleteMutation.isPending ? "Removing…" : "Remove"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      }
    />
  );
}
